import React from "react"
import { cn } from "@/lib/utils"
import { MobileTooltip, MobileTooltipContent, MobileTooltipTrigger, MobileTooltipProvider } from "@/components/ui/mobile-tooltip"
import { InformationCircleIcon } from "@heroicons/react/20/solid"

interface Props {
    id?: string
    children?: React.ReactNode
    className?: string
    titleClassName?: string
    title?: string
    subtitle?: string
    value?: string
    tooltip?: string
    icon?: React.ReactNode
    rightSideWrapper?: React.ReactNode
    badge?: React.ReactNode
    style?: React.CSSProperties
    noPadding?: boolean
}

// Value card header - for metrics with large values (Stripe-style compact)
const CardValueHeader = ({ value, title, subtitle, badge, className }: { value: string; title: string; subtitle?: string; badge?: React.ReactNode; className?: string }) => (
    <div className={cn("px-4 pb-1 pt-3", className)}>
        <h2 className="text-dashboard-xs font-medium uppercase tracking-wide text-gray-400">{title}</h2>
        {value && (
            <div className="mt-0.5 flex items-baseline gap-2">
                <span className="text-2xl font-semibold tracking-tight text-gray-900">{value}</span>
                {badge && <span>{badge}</span>}
            </div>
        )}
        {subtitle && <p className="mt-0.5 text-dashboard-xs text-gray-500">{subtitle}</p>}
    </div>
)

// Standard card header - Stripe/Linear-style tight spacing
const CardHeader = ({ title, tooltip, icon, badge, rightSideWrapper, titleClassName }: { title: string; tooltip?: string; icon?: React.ReactNode; badge?: React.ReactNode; rightSideWrapper?: React.ReactNode; titleClassName?: string }) => (
    <div className="flex items-center justify-between px-4 pb-0 pt-3">
        <div className="flex items-center gap-2">
            <h2 className={cn("text-base font-medium text-gray-900", titleClassName)}>{title}</h2>
            {badge && <span>{badge}</span>}
            {tooltip && (
                <MobileTooltipProvider delayDuration={200}>
                    <MobileTooltip delayDuration={200}>
                        <MobileTooltipTrigger asChild>
                            <button className="text-gray-300 transition-colors hover:text-gray-400 focus:outline-none">
                                <InformationCircleIcon className="h-4 w-4" />
                            </button>
                        </MobileTooltipTrigger>
                        <MobileTooltipContent side="top" className="max-w-xs">
                            {tooltip}
                        </MobileTooltipContent>
                    </MobileTooltip>
                </MobileTooltipProvider>
            )}
        </div>

        {rightSideWrapper && <div className="flex items-center">{rightSideWrapper}</div>}
    </div>
)

const Card: React.FC<Props> = ({ children, className, title, subtitle, value, tooltip, icon, rightSideWrapper, badge, titleClassName, id, style, noPadding = false }) => (
    <div className={cn("card flex flex-col overflow-hidden !p-0", className)} id={id} style={style}>
        {title && value ? <CardValueHeader value={value} title={title} subtitle={subtitle} badge={badge} className={titleClassName} /> : title ? <CardHeader title={title} tooltip={tooltip} icon={icon} badge={badge} rightSideWrapper={rightSideWrapper} titleClassName={titleClassName} /> : null}
        <div className={cn("flex-1 min-h-0", noPadding ? "" : "px-4 pb-3 pt-2")}>{children}</div>
    </div>
)

export default Card
